import { cloneElement, useEffect, useMemo, useState } from "react";
import { Pencil, Settings2, Check, RotateCcw, Filter, X, Move, Search, XCircle, Trash2 } from "lucide-react";
import { fmtDate, formatIDR, PROJECT_STATUS } from "@/lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import DraggableTh from "./DraggableTh";
import { useTableLayoutV2 } from "@/hooks/useTableLayoutV2";
import TableViewSettings from "@/components/table/TableViewSettings";
import SyncStickyTableHead from "@/components/table/SyncStickyTableHead";
import { computeRetentionHeld, computeRetentionClaimed, tokenize, matchTokens } from "./projectHelpers";

// Column definitions — order defines table order. Kolom Actions selalu tampil di luar loop.
const COLUMNS = [
  { key: "outlet", label: "Outlet", sortable: true },
  { key: "wo_number", label: "WO Number", sortable: true },
  { key: "wo_title", label: "Judul WO", sortable: false },
  { key: "po_number", label: "PO Number", sortable: true },
  { key: "po_value", label: "PO Value", sortable: true },
  { key: "sph_submit_date", label: "SPH Submit (Latest)", sortable: true },
  { key: "site_survey_date", label: "Site Survey Date", sortable: true },
  { key: "project_done_date", label: "Project Done Date", sortable: true },
  { key: "invoice_dates", label: "Invoice Dates (dari Ledger)", sortable: true },
  { key: "invoice_count", label: "Invoice Terkirim", sortable: true },
  { key: "retention_pct", label: "Retention %", sortable: true },
  { key: "retention_due", label: "Jatuh Tempo Retention", sortable: true },
  { key: "status", label: "Status", sortable: true },
];

const LS_COL_KEY = "ens_wo_hidden_cols_v1";
const LS_OUTLET_KEY = "ens_wo_outlet_filter_v1";

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch { return fallback; }
}

function outletKey(p) {
  return (p.parent_project_name || p.project_name || "—").trim() || "—";
}

function WOMonitoringView({ projects, onOpenEdit, onBulkChangeParent, onDelete }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "wo_date", dir: "desc" });
  const [selectedOutlets, setSelectedOutlets] = useState(() => loadJSON(LS_OUTLET_KEY, []));
  const [outletPopoverOpen, setOutletPopoverOpen] = useState(false);
  // 149-ak: multi-select untuk bulk pindah parent
  const [selectedWoIds, setSelectedWoIds] = useState(() => new Set());
  const toggleWoId = (id) => setSelectedWoIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedWoIds(new Set());
  useEffect(() => { try { localStorage.setItem(LS_OUTLET_KEY, JSON.stringify(selectedOutlets)); } catch {} }, [selectedOutlets]);

  // 149-as: layout v2 — replace old localStorage-only hook with server-synced version.
  // Bawa hidden, density, order, widths dalam satu source of truth per-user per-page.
  const layout = useTableLayoutV2("wo_monitoring", COLUMNS, { minWidth: 90, maxWidth: 480 });
  const isVisible = (k) => !layout.isHidden(k);
  const visibleColumns = layout.columns;
  const visibleOutletChips = useMemo(
    () => selectedOutlets.filter((s) => s !== "__none__").slice(0, 4),
    [selectedOutlets]
  );
  const hiddenCount = Object.keys(layout.hidden).filter((k) => layout.hidden[k]).length;
  const visibleColCount = visibleColumns.length;
  // Density styling — apply to tbody rows; header tetap. Compact = padding lebih sempit.
  const rowPadClass = layout.density === "compact" ? "px-2 py-1" : "px-3 py-2";
  // 149-at: sticky pin map — kolom yg di-pin dapat `{ side, offset }` untuk `position: sticky`.
  const stickyMap = useMemo(() => layout.computeSticky(), [layout]);
  // Helper: wrap <td> returned by renderCell dgn sticky style saat kolom di-pin.
  // Sticky perlu solid bg agar tidak bocor saat scroll — pakai bg-white + shadow indikator.
  const applyStickyToCell = (cell, key) => {
    if (!cell) return cell;
    const sticky = stickyMap.get(key);
    if (!sticky) return cell;
    const extraStyle = {
      position: "sticky",
      [sticky.side]: `${sticky.offset}px`,
      zIndex: 5,
      backgroundColor: "#ffffff",
      boxShadow: sticky.side === "left" ? "2px 0 4px -2px rgba(11,29,58,0.15)" : "-2px 0 4px -2px rgba(11,29,58,0.15)",
    };
    const prevClass = cell.props.className || "";
    return cloneElement(cell, {
      style: { ...(cell.props.style || {}), ...extraStyle },
      className: `${prevClass} wo-sticky-cell`.trim(),
      "data-pin": sticky.side,
    });
  };

  const wos = projects.filter((p) => p.project_type === "working_order");
  // 16-PROJECT-PARENT-CLEANUP-R1-R1: outlet dropdown SOURCE = master parent
  // maintenance (bukan distinct dari WO). Parent aktif tanpa WO tetap muncul.
  const maintenanceParents = useMemo(
    () => projects.filter((p) =>
      p.project_type === "new_project" && p.is_outlet === true &&
      !p.parent_project_id && !p.is_archived   // 149-cw-r2: exclude archived
    ),
    [projects]
  );
  // Union of parent-maintenance master + distinct outletKey dari WO (legacy WO
  // whose parent belum di-flag is_outlet=true tetap terjaring).
  const outletList = useMemo(() => {
    const s = new Set();
    for (const p of maintenanceParents) s.add((p.project_name || "—").trim() || "—");
    for (const w of wos) s.add(outletKey(w));
    return Array.from(s).sort((a, b) => a.localeCompare(b, "id"));
  }, [maintenanceParents, wos]);
  const outletFilterActive = selectedOutlets.length > 0 && selectedOutlets.length < outletList.length;
  const toggleOutlet = (name) => setSelectedOutlets((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);
  const selectAllOutlets = () => setSelectedOutlets([]);
  const clearOutlets = () => setSelectedOutlets([]);

  const filteredBase = wos.filter((p) => {
    if (outletFilterActive && !selectedOutlets.includes(outletKey(p))) return false;
    if (!q.trim()) return true;
    // Multi-kata AND tokenized search (2026-02-15 UX sync).
    const tokens = tokenize(q);
    const hay = [
      p.wo_number, p.wo_title, p.po_number,
      p.parent_project_name, p.project_name, p.client, p.city, p.province,
    ].filter(Boolean).join(" ");
    return matchTokens(hay, tokens);
  });
  // Aggregate stats per-outlet (across ALL WO of that outlet, ignoring current filter).
  // Used both by the summary cards strip and inside the popover badges.
  const outletStats = useMemo(() => {
    // today di-hitung di dalam useMemo supaya deps array tetap `[wos]` saja (tidak
    // re-compute tiap render). Cukup akurat: berubah setiap paint pertama harian.
    const today = new Date();
    const m = new Map();
    for (const w of wos) {
      const name = outletKey(w);
      if (!m.has(name)) m.set(name, { name, count: 0, poTotal: 0, active: 0, overdue: 0 });
      const s = m.get(name);
      s.count += 1;
      s.poTotal += Number(w.po_value) || 0;
      if (w.status === "active") s.active += 1;
      // Overdue: end_date lewat & belum completed. Use project_done_date sebagai proxy end.
      // Kalau ada retention_period_days & completed_at & retentionOutstanding, treat sebagai overdue retensi.
      const endRaw = w.project_done_date || w.end_date;
      if (w.status !== "completed" && w.status !== "cancelled" && endRaw) {
        const d = new Date(String(endRaw).slice(0, 10) + "T00:00:00");
        if (!isNaN(d.getTime()) && d < today) s.overdue += 1;
      }
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name, "id"));
  }, [wos]);

  // `today` untuk aging checks di JSX (di bawah); di-scope render agar konsisten
  // dengan tanggal per repaint. useMemo `outletStats` declare `today`-nya sendiri.
  const today = new Date();

  // Card click handler — mirror behavior of the popover: convert from "all-visible" mode
  // to explicit include list on first toggle so card + panel stay in sync.
  const handleOutletCardClick = (name) => {
    if (selectedOutlets.length === 0) {
      // All visible → clicking one card = ONLY that outlet (quick focus)
      setSelectedOutlets([name]);
    } else if (selectedOutlets.length === 1 && selectedOutlets[0] === "__none__") {
      setSelectedOutlets([name]);
    } else if (selectedOutlets.includes(name)) {
      const next = selectedOutlets.filter((n) => n !== name);
      // If clicking the only selected outlet, return to "all-visible" mode
      setSelectedOutlets(next.length === 0 ? [] : next);
    } else {
      setSelectedOutlets([...selectedOutlets, name]);
    }
  };
  const isOutletActiveInCard = (name) => {
    if (selectedOutlets.length === 0) return true; // all-visible
    if (selectedOutlets[0] === "__none__") return false;
    return selectedOutlets.includes(name);
  };

  const woWithDue = filteredBase.map((p) => {
    const retPeriod = Number(p.retention_period_days) || 0;
    let dueDate = null;
    if (p.status === "completed" && retPeriod > 0 && p.completed_at) {
      const d = new Date(p.completed_at);
      d.setDate(d.getDate() + retPeriod);
      dueDate = d;
    }
    return { ...p, __due_date: dueDate };
  });
  const filtered = [...woWithDue].sort((a, b) => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const pick = (p) => {
      switch (sort.key) {
        case "outlet": return outletKey(p).toLowerCase();
        case "wo_number": return (p.wo_number || "").toLowerCase();
        case "po_number": return (p.po_number || "").toLowerCase();
        case "po_value": return Number(p.po_value) || 0;
        case "sph_submit_date": return p.sph_submit_date || "";
        case "site_survey_date": return p.site_survey_date || "";
        case "project_done_date": return p.project_done_date || "";
        case "invoice_dates": {
          // Sort by earliest invoice date (from ledger). Empty → sort last.
          const dates = (p.income_entries || [])
            .map((e) => e.invoice_date)
            .filter(Boolean)
            .map((d) => String(d).slice(0, 10))
            .sort();
          return dates.length ? dates[0] : "z";
        }
        case "retention_pct": return Number(p.retention_pct) || 0;
        case "invoice_count": return (p.income_entries || []).filter((e) => e.invoice_date).length;
        case "retention_due": return p.__due_date ? p.__due_date.toISOString() : "z";
        case "status": return p.status || "";
        case "wo_date":
        default: return p.wo_date || "";
      }
    };
    const va = pick(a); const vb = pick(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  return (
    <div className="space-y-4" data-testid="wo-view">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="bg-slate-50 border border-slate-200 p-3">
          <div className="overline text-slate-600">Total WO</div>
          <div className="font-display text-lg tabular-nums text-[#0B1D3A]">{wos.length}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 p-3">
          <div className="overline text-slate-600">Total PO</div>
          <div className="font-display text-lg tabular-nums text-[#0B1D3A]" data-testid="wo-total-po-count" title="Jumlah PO Number unik (PO Number yang sama dihitung sekali)">
            {(() => {
              // User request 10-Feb-2026: total PO unik. Kalau ada PO Number sama, dihitung 1.
              // WO tanpa PO Number tidak dihitung (karena tidak punya PO).
              const uniquePOs = new Set();
              for (const w of wos) {
                const po = (w.po_number || "").trim();
                if (po) uniquePOs.add(po);
              }
              return uniquePOs.size;
            })()}
          </div>
        </div>
        <div className="bg-sky-50 border border-sky-200 p-3">
          <div className="overline text-sky-700">Total PO Value</div>
          <div className="font-display text-lg tabular-nums text-sky-800" data-testid="wo-total-po">
            {formatIDR(wos.reduce((s, p) => s + (Number(p.po_value) || 0), 0))}
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 p-3">
          <div className="overline text-amber-700">WO Aktif</div>
          <div className="font-display text-lg tabular-nums text-amber-800">{wos.filter((p) => p.status === "active").length}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 p-3">
          <div className="overline text-emerald-700">Selesai</div>
          <div className="font-display text-lg tabular-nums text-emerald-800">{wos.filter((p) => p.status === "completed").length}</div>
        </div>
      </div>

      {/* Outlet Summary Cards — horizontal scroll strip, each card acts as quick filter */}
      {outletStats.length > 0 && (
        <div className="space-y-1.5" data-testid="wo-outlet-cards-strip">
          <div className="flex items-center justify-between">
            <div className="overline text-slate-600">Ringkasan per Outlet · klik kartu untuk filter</div>
            {outletFilterActive && (
              <button
                type="button"
                data-testid="wo-outlet-cards-reset"
                onClick={clearOutlets}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#FF5A1F]"
              >
                <RotateCcw size={10} /> Tampilkan Semua
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {outletStats.map((o) => {
              const active = isOutletActiveInCard(o.name);
              return (
                <button
                  key={o.name}
                  type="button"
                  data-testid={`wo-outlet-card-${o.name}`}
                  onClick={() => handleOutletCardClick(o.name)}
                  className={`flex-shrink-0 w-56 text-left border-2 px-3 py-2 transition-colors ${active
                    ? "border-[#FF5A1F] bg-orange-50 hover:bg-orange-100"
                    : "border-slate-200 bg-white opacity-60 hover:opacity-100 hover:border-slate-400"}`}
                  title={active ? "Klik untuk hilangkan filter outlet ini" : "Klik untuk fokus ke outlet ini"}
                >
                  <div className="flex items-start justify-between gap-1">
                    <div className={`font-semibold text-xs truncate ${active ? "text-[#0B1D3A]" : "text-slate-600"}`}>{o.name}</div>
                    <span className={`text-[9px] font-bold px-1 py-0.5 tabular-nums whitespace-nowrap ${active ? "bg-[#FF5A1F] text-white" : "bg-slate-100 text-slate-600"}`}>
                      {o.count} WO
                    </span>
                  </div>
                  <div className="mt-1.5 text-[10px] tabular-nums text-slate-500 truncate" title={`Total PO Value: ${formatIDR(o.poTotal)}`}>
                    PO <span className={`font-semibold ${active ? "text-sky-700" : "text-slate-700"}`}>{formatIDR(o.poTotal)}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px]">
                    <span className="tabular-nums">
                      <span className="text-emerald-700 font-semibold">{o.active}</span>
                      <span className="text-slate-400"> aktif</span>
                    </span>
                    {o.overdue > 0 && (
                      <span className="tabular-nums" data-testid={`wo-outlet-card-overdue-${o.name}`}>
                        <span className="text-rose-700 font-semibold">{o.overdue}</span>
                        <span className="text-rose-500"> overdue</span>
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selection toolbar — muncul saat ada WO tercentang (149-ak) */}
      {selectedWoIds.size > 0 && (
        <div className="flex items-center gap-3 bg-[#0B1D3A] text-white px-4 py-2 border-l-4 border-l-[#FF5A1F]" data-testid="wo-selection-toolbar">
          <span className="overline text-white/80 text-[10px]">
            <span className="font-bold text-white tabular-nums text-sm">{selectedWoIds.size}</span> WO Terpilih
          </span>
          <button
            type="button"
            data-testid="wo-bulk-change-parent-btn"
            onClick={() => {
              const chosen = wos.filter((w) => selectedWoIds.has(w.id));
              onBulkChangeParent?.(chosen, clearSelection);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-[11px] uppercase tracking-wider font-semibold"
          >
            <Move size={12} /> Pindah Parent
          </button>
          <button
            type="button"
            data-testid="wo-bulk-clear"
            onClick={clearSelection}
            className="text-[10px] uppercase tracking-wider text-white/70 hover:text-white ml-auto"
          >
            × Batalkan Pilihan
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {/* Search box — konsisten dgn Projects / AR search (2026-02-15). */}
        <div className="relative flex items-center" data-testid="wo-search-wrap">
          <Search size={13} className="absolute left-2.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari WO / judul / PO / outlet / client…"
            className="pl-8 pr-8 py-2 border border-slate-300 text-xs w-80 focus:outline-none focus:border-[#FF5A1F] bg-white placeholder:text-slate-400"
            data-testid="wo-search"
          />
          {q && (
            <button
              type="button"
              data-testid="wo-search-clear"
              onClick={() => setQ("")}
              title="Bersihkan pencarian"
              className="absolute right-2 text-slate-400 hover:text-[#FF5A1F]"
            >
              <XCircle size={14} />
            </button>
          )}
        </div>
        {/* Outlet filter dropdown */}
        <Popover open={outletPopoverOpen} onOpenChange={setOutletPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="wo-outlet-filter-btn"
              className={`inline-flex items-center gap-1.5 h-9 px-3 border text-xs uppercase tracking-wider font-semibold ${outletFilterActive ? "border-[#FF5A1F] bg-orange-50 text-[#FF5A1F]" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              title="Filter outlet"
            >
              <Filter size={13} />
              Outlet
              {outletFilterActive && (
                <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold bg-[#FF5A1F] text-white tabular-nums" data-testid="wo-outlet-active-count">
                  {selectedOutlets.length}/{outletList.length}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-0 rounded-none border-slate-300" data-testid="wo-outlet-panel">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="overline text-slate-600">Filter Outlet ({outletList.length})</div>
              <button
                type="button"
                data-testid="wo-outlet-reset"
                disabled={!outletFilterActive}
                onClick={clearOutlets}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-[#FF5A1F] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Tampilkan semua outlet"
              >
                <RotateCcw size={10} /> Reset
              </button>
            </div>
            <div className="px-3 py-2 border-b border-slate-100 bg-white flex gap-2 flex-wrap">
              <button
                type="button"
                data-testid="wo-outlet-select-all"
                onClick={selectAllOutlets}
                className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                Semua Outlet
              </button>
              <button
                type="button"
                data-testid="wo-outlet-clear"
                onClick={() => setSelectedOutlets(["__none__"])}
                className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-slate-300 text-slate-600 hover:bg-slate-50"
                title="Kosongkan tabel (pilih tidak ada outlet)"
              >
                Kosongkan
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto py-1">
              {outletList.length === 0 && (
                <div className="px-3 py-3 text-xs text-slate-400 italic">Belum ada outlet.</div>
              )}
              {outletList.map((name) => {
                const active = selectedOutlets.length === 0 ? true : selectedOutlets.includes(name);
                const count = wos.filter((w) => outletKey(w) === name).length;
                return (
                  <button
                    key={name}
                    type="button"
                    data-testid={`wo-outlet-toggle-${name}`}
                    onClick={() => {
                      // Convert from "all-visible" mode to explicit include list on first toggle
                      if (selectedOutlets.length === 0) {
                        setSelectedOutlets(outletList.filter((n) => n !== name));
                      } else if (selectedOutlets.length === 1 && selectedOutlets[0] === "__none__") {
                        setSelectedOutlets([name]);
                      } else {
                        toggleOutlet(name);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-3 py-1.5 text-xs hover:bg-slate-50 ${active ? "text-slate-800" : "text-slate-400"}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`h-4 w-4 border flex items-center justify-center flex-shrink-0 ${active ? "bg-emerald-600 border-emerald-700" : "bg-white border-slate-300"}`}>
                        {active && <Check size={11} className="text-white" strokeWidth={3} />}
                      </span>
                      <span className="font-medium truncate max-w-[200px]" title={name}>{name}</span>
                    </span>
                    <span className="text-[10px] text-slate-500 tabular-nums">{count} WO</span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        {/* 149-as: Standard TableViewSettings — replaces old "Atur Kolom" popover.
            Now includes: hide/unhide, density (compact/normal), reorder + width reset.
            State disimpan per-user per-page di backend `/user-prefs/table/wo_monitoring`. */}
        <TableViewSettings layout={layout} align="start" />

        {/* Active outlet chips (quick remove) */}
        {outletFilterActive && (
          <div className="flex items-center gap-1 flex-wrap ml-1" data-testid="wo-outlet-chips">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Menampilkan:</span>
            {visibleOutletChips.map((name) => (
              <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-orange-100 text-orange-800 border border-orange-300">
                {name}
                <button
                  type="button"
                  onClick={() => toggleOutlet(name)}
                  className="hover:text-rose-700"
                  title="Hilangkan filter"
                ><X size={9} /></button>
              </span>
            ))}
            {selectedOutlets.length > 4 && (
              <span className="text-[10px] text-slate-500 italic">+{selectedOutlets.length - 4} lain</span>
            )}
            {selectedOutlets[0] === "__none__" && (
              <span className="text-[10px] italic text-rose-600">Tidak ada outlet terpilih</span>
            )}
          </div>
        )}
      </div>
      {/* 149-cj: SyncStickyTableHead — portal-clone thead ke <body> sebagai
          fixed overlay. Wrapper tetap punya horizontal scroll (via ens-table-scroll
          yang inject di dalam komponen), tapi header sinkron ke viewport top:96px. */}
      <SyncStickyTableHead offset="context" className="bg-white border border-slate-200">
        <table className="w-full text-xs" style={{ tableLayout: layout.hasCustomLayout ? "fixed" : "auto" }}>
          <colgroup>
            {visibleColumns.map((c) => {
              const w = layout.getWidth(c.key);
              return <col key={c.key} style={w ? { width: `${w}px` } : undefined} />;
            })}
            <col />
          </colgroup>
          <thead className="bg-white border-b-2 border-slate-300">
            <tr className="text-left">
              {/* Multi-select column (149-ak) — checkbox utk pindah parent bulk */}
              <th className="px-2 py-3 bg-white w-8" data-testid="wo-th-select">
                <input
                  type="checkbox"
                  data-testid="wo-select-all"
                  checked={filtered.length > 0 && filtered.every((p) => selectedWoIds.has(p.id))}
                  ref={(el) => {
                    if (el) {
                      const some = filtered.some((p) => selectedWoIds.has(p.id));
                      const all = filtered.every((p) => selectedWoIds.has(p.id));
                      el.indeterminate = some && !all;
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedWoIds(new Set(filtered.map((p) => p.id)));
                    } else {
                      clearSelection();
                    }
                  }}
                  className="h-3.5 w-3.5 accent-[#FF5A1F] cursor-pointer"
                  title="Pilih semua WO yang tampil"
                />
              </th>
              {visibleColumns.map((c) => {
                const sticky = stickyMap.get(c.key);
                return (
                  <DraggableTh
                    key={c.key}
                    colKey={c.key}
                    label={c.label}
                    sortable={c.sortable}
                    sort={sort}
                    setSort={setSort}
                    width={layout.getWidth(c.key)}
                    onResize={(px) => layout.setWidth(c.key, px)}
                    onMove={layout.moveColumn}
                    testid={`wo-th-${c.key}`}
                    pinSide={sticky?.side}
                    pinOffset={sticky?.offset}
                  />
                );
              })}
              <th className="px-3 py-3 overline text-slate-700 whitespace-nowrap bg-white"></th>
            </tr>
          </thead>
          <tbody data-testid="wo-table">
            {filtered.length === 0 && <tr><td colSpan={visibleColCount + 2} className="px-4 py-6 text-center text-slate-400" data-testid="wo-empty-row">
              {selectedOutlets.length === 1
                ? "Belum ada Working Order pada parent/outlet ini."
                : "Tidak ada Working Order sesuai filter."}
            </td></tr>}
            {filtered.map((p) => {
              const retPct = Number(p.retention_pct) || 0;
              const retPeriod = Number(p.retention_period_days) || 0;
              // Retensi (149-ac): butuh parent's vo_aggregation utk formula split.
              const parent = p.parent_project_id ? projects.find((x) => x.id === p.parent_project_id) : p;
              const voAgg = (parent?.vo_aggregation) || "separate";
              const retentionHeld = computeRetentionHeld(p, voAgg);
              const retentionClaimed = computeRetentionClaimed(p);
              const retentionOutstanding = Math.max(0, retentionHeld - retentionClaimed);
              const isCompletedWithRetention = p.status === "completed" && retentionOutstanding > 0.01;
              const invoiceEntries = (p.income_entries || []).filter((e) => e.invoice_date && !e.is_retention_claim);
              const invoiceReceivedCount = invoiceEntries.filter((e) => !!e.date).length;
              const invoiceOutstandingCount = invoiceEntries.length - invoiceReceivedCount;
              const invoiceSentTotal = invoiceEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
              let retentionDueDate = null;
              let retentionOverdue = false;
              if (isCompletedWithRetention && retPeriod > 0 && p.completed_at) {
                const d = new Date(p.completed_at);
                d.setDate(d.getDate() + retPeriod);
                retentionDueDate = d;
                retentionOverdue = today > d;
              }
              const renderCell = (key) => {
                switch (key) {
                  case "outlet":
                    return <td key={key} className="px-3 py-2 text-slate-700 font-medium max-w-[180px] truncate" title={outletKey(p)} data-testid={`wo-outlet-cell-${p.id}`}>{outletKey(p)}</td>;
                  case "wo_number":
                    return <td key={key} className="px-3 py-2 font-mono font-semibold text-[#0B1D3A]">{p.wo_number || "-"}</td>;
                  case "wo_title":
                    return <td key={key} className="px-3 py-2 text-slate-700 max-w-xs truncate" title={p.wo_title || ""} data-testid={`wo-title-cell-${p.id}`}>
                      {p.wo_title ? p.wo_title : <span className="text-slate-300">—</span>}
                    </td>;
                  case "po_number":
                    return <td key={key} className="px-3 py-2 font-mono text-slate-700">{p.po_number || <span className="text-slate-300">—</span>}</td>;
                  case "po_value":
                    return <td key={key} className="px-3 py-2 tabular-nums text-slate-700">{p.po_value != null ? formatIDR(p.po_value) : "-"}</td>;
                  case "sph_submit_date":
                    return <td key={key} className="px-3 py-2 tabular-nums">{fmtDate(p.sph_submit_date) || <span className="text-slate-300">—</span>}</td>;
                  case "site_survey_date":
                    return <td key={key} className="px-3 py-2 tabular-nums">{fmtDate(p.site_survey_date) || <span className="text-slate-300">—</span>}</td>;
                  case "project_done_date":
                    return <td key={key} className="px-3 py-2 tabular-nums">{fmtDate(p.project_done_date) || <span className="text-slate-300">—</span>}</td>;
                  case "invoice_dates": {
                    // Sync dari income_entries (source of truth) — auto-populated saat user
                    // input entry di Income Ledger. Ditampilkan sebagai list "Inv N: dd MMM yyyy"
                    // dengan color-coded status: emerald kalau sudah received, amber kalau waiting.
                    // User request 10-Feb-2026: "rubah konsep RETENTION INVOICE DATE. Buatkan
                    // kolom isian invoice date 1, 2 dan seterusnya. sinkronkan dengan data uang masuk".
                    const invoices = (p.income_entries || [])
                      .filter((e) => e.invoice_date)
                      .map((e) => ({ date: String(e.invoice_date).slice(0, 10), received: !!e.date, isClaim: !!e.is_retention_claim, amount: Number(e.amount) || 0 }))
                      .sort((a, b) => a.date.localeCompare(b.date));
                    return (
                      <td key={key} className="px-3 py-2 tabular-nums" data-testid={`wo-invoice-dates-${p.id}`}>
                        {invoices.length === 0 ? (
                          <span className="text-slate-300 text-[10px] italic">Belum ada invoice</span>
                        ) : (
                          <div className="flex flex-col gap-0.5 text-[10px]" style={{ maxHeight: "6.5em", overflowY: "auto" }}>
                            {invoices.slice(0, 8).map((inv, idx) => (
                              <div key={`${inv.date || 'no-date'}-${inv.amount || 0}-${idx}`} className="whitespace-nowrap" title={`${formatIDR(inv.amount)}${inv.received ? " · Diterima" : " · Waiting"}${inv.isClaim ? " · Retention Claim" : ""}`}>
                                <span className="text-slate-500">Inv {idx + 1}:</span>{" "}
                                <span className={inv.received ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                                  {fmtDate(inv.date)}
                                </span>
                                {inv.isClaim && <span className="ml-1 text-[8px] uppercase font-bold text-amber-600">RC</span>}
                              </div>
                            ))}
                            {invoices.length > 8 && (
                              <div className="text-[9px] text-slate-400 italic">+{invoices.length - 8} lain</div>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  }
                  case "invoice_count":
                    return (
                      <td key={key} className="px-3 py-2" data-testid={`wo-invoice-count-${p.id}`}>
                        {invoiceEntries.length === 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <div className="text-[11px] leading-tight">
                            <span className="inline-flex items-center gap-1 font-semibold text-[#0B1D3A]">{invoiceEntries.length}<span className="text-[9px] font-normal text-slate-500">invoice</span></span>
                            <div className="text-[9px] text-slate-500 tabular-nums">
                              <span className="text-emerald-700">✓ {invoiceReceivedCount} diterima</span>
                              {invoiceOutstandingCount > 0 && <span className="text-amber-700 ml-1">· {invoiceOutstandingCount} outstanding</span>}
                            </div>
                            <div className="text-[9px] text-slate-500 tabular-nums">Rp {invoiceSentTotal.toLocaleString("id-ID")}</div>
                          </div>
                        )}
                      </td>
                    );
                  case "retention_pct":
                    return (
                      <td key={key} className="px-3 py-2 tabular-nums">
                        {retPct > 0 ? (
                          <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">{retPct}%</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    );
                  case "retention_due":
                    return (
                      <td key={key} className="px-3 py-2 tabular-nums">
                        {retentionDueDate ? (
                          <span className={retentionOverdue ? "text-rose-700 font-semibold" : "text-amber-700"}>
                            {fmtDate(retentionDueDate.toISOString())}
                            {retentionOverdue && <span className="ml-1 text-[9px] uppercase font-bold">TERLAMBAT</span>}
                            <div className="text-[9px] text-slate-500 font-normal">Outstanding: {formatIDR(retentionOutstanding)}</div>
                          </span>
                        ) : isCompletedWithRetention && retPeriod === 0 ? (
                          <span className="text-[10px] text-slate-500 italic">Set masa retensi</span>
                        ) : p.status === "completed" ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">belum selesai</span>
                        )}
                      </td>
                    );
                  case "status":
                    return (
                      <td key={key} className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${(PROJECT_STATUS[p.status] || PROJECT_STATUS.active).cls}`}>
                          {(PROJECT_STATUS[p.status] || PROJECT_STATUS.active).label}
                        </span>
                      </td>
                    );
                  default:
                    return null;
                }
              };
              const isSelected = selectedWoIds.has(p.id);
              return (
                <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${isSelected ? "bg-orange-50/60" : ""} ${layout.density === "compact" ? "[&>td]:!py-1 [&>td]:!text-[11px]" : ""}`} data-testid={`wo-row-${p.id}`}>
                  <td className="px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      data-testid={`wo-select-${p.id}`}
                      checked={isSelected}
                      onChange={() => toggleWoId(p.id)}
                      className="h-3.5 w-3.5 accent-[#FF5A1F] cursor-pointer"
                    />
                  </td>
                  {visibleColumns.map((c) => applyStickyToCell(renderCell(c.key), c.key))}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => onOpenEdit(p)} className="text-[10px] uppercase tracking-wider font-semibold text-sky-700 hover:underline" data-testid={`wo-edit-${p.id}`}>
                        <Pencil size={10} className="inline -mt-0.5 mr-1" /> Edit
                      </button>
                      {onDelete && (
                        <button onClick={() => onDelete(p)} className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 hover:underline" data-testid={`wo-delete-${p.id}`} title="Hapus WO — butuh alasan wajib">
                          <Trash2 size={10} className="inline -mt-0.5 mr-1" /> Hapus
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </SyncStickyTableHead>
    </div>
  );
}

export default WOMonitoringView;
